/**
 * useLiveSessionContext - Live session context and contextual-chat payloads
 *
 * Extracts the live session context builder and contextual runtime payload
 * shaping from App.tsx so the core live controller can depend on a focused API.
 */

import { MutableRefObject, useCallback } from 'react';

import type { AvatarSessionContext, AvatarSessionStatus } from '../../session';
import { createLiveSessionContext, resolveLiveSessionKindFromMetadata } from '../../session/liveMode';

export interface UseLiveSessionContextOptions {
  builderDiagramId?: string;
  liveConnectionError: string | null;
  liveProvisionUserId?: string;
  liveSessionContext: AvatarSessionContext | null;
  liveSessionContextRef: MutableRefObject<AvatarSessionContext | null>;
  liveSessionStatus?: AvatarSessionStatus;
  projectTokenCamlin?: string;
  setLiveSessionContext: (context: AvatarSessionContext | null) => void;
  setLiveStatus: (
    status: AvatarSessionStatus,
    context?: AvatarSessionContext | null,
    lastError?: string
  ) => void;
}

export interface UseLiveSessionContextReturn {
  ensureLiveContext: (overrides?: Partial<AvatarSessionContext>) => AvatarSessionContext;
  buildContextualChatLiveRuntimeContext: (context: AvatarSessionContext | null) => Record<string, unknown> | null;
}

function normalizeConversationHistory(
  history: unknown
): Array<{ role: 'designer' | 'assistant'; content: string }> {
  if (!Array.isArray(history)) {
    return [];
  }

  return history
    .map((turn) => {
      if (!turn || typeof turn !== 'object') {
        return null;
      }

      const rawTurn = turn as Record<string, unknown>;
      const role = rawTurn.role === 'assistant'
        ? 'assistant'
        : rawTurn.role === 'designer' || rawTurn.role === 'user'
          ? 'designer'
          : null;
      const content = typeof rawTurn.content === 'string' ? rawTurn.content.trim() : '';
      if (!role || !content) {
        return null;
      }

      return { role, content };
    })
    .filter((turn): turn is { role: 'designer' | 'assistant'; content: string } => turn !== null)
    .slice(-8);
}

export function useLiveSessionContext({
  builderDiagramId,
  liveConnectionError,
  liveProvisionUserId,
  liveSessionContext,
  liveSessionContextRef,
  liveSessionStatus,
  projectTokenCamlin,
  setLiveSessionContext,
  setLiveStatus
}: UseLiveSessionContextOptions): UseLiveSessionContextReturn {
  const ensureLiveContext = useCallback((overrides: Partial<AvatarSessionContext> = {}) => {
    const nextContext = createLiveSessionContext({
      ...(liveSessionContext ?? {}),
      projectToken: overrides.projectToken ?? liveSessionContext?.projectToken ?? projectTokenCamlin ?? undefined,
      diagramId: overrides.diagramId ?? liveSessionContext?.diagramId ?? builderDiagramId ?? undefined,
      handoffToken: overrides.handoffToken ?? liveSessionContext?.handoffToken ?? undefined,
      sessionId: overrides.sessionId ?? liveSessionContext?.sessionId ?? undefined,
      tenantId: overrides.tenantId ?? liveSessionContext?.tenantId,
      userId: overrides.userId ?? liveSessionContext?.userId ?? liveProvisionUserId,
      metadata: {
        ...(liveSessionContext?.metadata ?? {}),
        ...(overrides.metadata ?? {}),
        liveMode: true
      }
    }, builderDiagramId);

    setLiveSessionContext(nextContext);
    liveSessionContextRef.current = nextContext;
    setLiveStatus(liveSessionStatus ?? 'idle', nextContext, liveConnectionError ?? undefined);
    (window as any).__CAMLIN_AVATAR_SESSION_CONTEXT__ = nextContext;
    return nextContext;
  }, [
    builderDiagramId,
    liveConnectionError,
    liveProvisionUserId,
    liveSessionContext,
    liveSessionContextRef,
    liveSessionStatus,
    projectTokenCamlin,
    setLiveSessionContext,
    setLiveStatus
  ]);

  const buildContextualChatLiveRuntimeContext = useCallback((context: AvatarSessionContext | null) => {
    const metadata = context?.metadata && typeof context.metadata === 'object'
      ? context.metadata as Record<string, unknown>
      : null;
    const liveSessionKind = resolveLiveSessionKindFromMetadata(metadata);

    if (!metadata || liveSessionKind !== 'contextual-chat') {
      return null;
    }

    const assistantProfile = metadata.assistantProfile && typeof metadata.assistantProfile === 'object'
      ? metadata.assistantProfile as Record<string, unknown>
      : null;
    const contextSummary = typeof metadata.contextSummary === 'string' && metadata.contextSummary.trim()
      ? metadata.contextSummary.trim()
      : typeof metadata.contextSheet === 'string' && metadata.contextSheet.trim()
        ? metadata.contextSheet.trim()
        : '';
    const contextId = typeof metadata.contextId === 'string' && metadata.contextId.trim()
      ? metadata.contextId.trim()
      : typeof metadata.contextDiagramId === 'string' && metadata.contextDiagramId.trim()
        ? metadata.contextDiagramId.trim()
        : '';
    const interactionId = typeof metadata.interactionId === 'string' && metadata.interactionId.trim()
      ? metadata.interactionId.trim()
      : typeof metadata.contextInteractionId === 'string' && metadata.contextInteractionId.trim()
        ? metadata.contextInteractionId.trim()
        : '';
    const hostContext = metadata.hostContext && typeof metadata.hostContext === 'object'
      ? metadata.hostContext as Record<string, unknown>
      : null;
    const conversationHistory = normalizeConversationHistory(metadata.conversationHistory);

    const payload: Record<string, unknown> = {
      liveSessionKind: 'contextual-chat',
      contextualLive: true
    };

    const assistantId = typeof assistantProfile?.id === 'string' && assistantProfile.id.trim()
      ? assistantProfile.id.trim()
      : typeof metadata.specialistId === 'string' && metadata.specialistId.trim()
        ? metadata.specialistId.trim()
        : '';
    if (assistantId) {
      payload.specialistId = assistantId;
    }
    const assistantName = typeof assistantProfile?.name === 'string' && assistantProfile.name.trim()
      ? assistantProfile.name.trim()
      : typeof metadata.specialistName === 'string' && metadata.specialistName.trim()
        ? metadata.specialistName.trim()
        : '';
    if (assistantName) {
      payload.specialistName = assistantName;
    }
    const assistantRole = typeof assistantProfile?.role === 'string' && assistantProfile.role.trim()
      ? assistantProfile.role.trim()
      : typeof metadata.specialistRole === 'string' && metadata.specialistRole.trim()
        ? metadata.specialistRole.trim()
        : '';
    if (assistantRole) {
      payload.specialistRole = assistantRole;
    }
    const assistantPrompt = typeof assistantProfile?.prompt === 'string' && assistantProfile.prompt.trim()
      ? assistantProfile.prompt.trim()
      : typeof metadata.specialistPrompt === 'string' && metadata.specialistPrompt.trim()
        ? metadata.specialistPrompt.trim()
        : '';
    if (assistantPrompt) {
      payload.specialistPrompt = assistantPrompt;
    }
    if (contextSummary) {
      payload.contextSheet = contextSummary;
      payload.contextSummary = contextSummary;
    }
    if (contextId) {
      payload.contextDiagramId = contextId;
      payload.contextId = contextId;
    }
    if (interactionId) {
      payload.contextInteractionId = interactionId;
      payload.interactionId = interactionId;
    }
    if (hostContext) {
      payload.hostContext = hostContext;
    }
    if (conversationHistory.length > 0) {
      payload.conversationHistory = conversationHistory;
    }

    // Host identifier — runtime routes external hosts (agentlink, clawlink,
    // camlin-architect, etc.) through /generate/host-chat so the host's
    // specialistPrompt is the SOLE system message (no CamlinConnect
    // workbench framing). When undefined or 'builder', runtime uses
    // /generate/designer-chat which augments the workbench persona —
    // correct only for the in-builder ContextualChatPanel.
    const host = typeof metadata.host === 'string' && metadata.host.trim()
      ? (metadata.host as string).trim()
      : '';
    if (host) {
      payload.host = host;
    }

    // Optional verbatim spoken-greeting override. The runtime stores
    // this on `state.contextualLive.greeting` and, when present, uses
    // it as the avatar's first spoken line instead of synthesising one
    // from its built-in template — the only way for hosts to fully
    // control the greeting (e.g. "Hi <name>, how can I help …") and
    // avoid leaking unrelated product names like "Decision Memory".
    const greeting = typeof metadata.greeting === 'string' && metadata.greeting.trim()
      ? (metadata.greeting as string).trim()
      : '';
    if (greeting) {
      payload.greeting = greeting;
    }

    // Friendly user display name. Used by the runtime's fallback
    // greeting (when no `greeting` override is supplied) to address
    // the operator by name. Worth shipping even when `greeting` is
    // also set so future runtime-side changes can personalise other
    // synthesised lines (re-engagement, re-prompt, etc.).
    const userDisplayName = typeof metadata.userDisplayName === 'string' && metadata.userDisplayName.trim()
      ? (metadata.userDisplayName as string).trim()
      : '';
    if (userDisplayName) {
      payload.userDisplayName = userDisplayName;
    }

    // Host-managed voice mode: when true, the runtime will NOT invoke its
    // own LLM on `voice.transcript.final`. The host is responsible for
    // resolving the answer (RAG, intent, etc.) and pushing it into TTS
    // via the `avatar:live:speak` event. See `useHostManagedVoiceTurn`
    // for the standard wiring helper, and ContextualLiveState
    // `suppressRuntimeAutoResponse` in the runtime for the consuming side.
    if (metadata.suppressRuntimeAutoResponse === true) {
      payload.suppressRuntimeAutoResponse = true;
    }

    // Camlin Companion (doc 116 CO1/CO2) — host='companion' turns are
    // routed to schema-service /generate/companion-chat, which REFUSES a
    // session with no siteKey; visitorId and pageContext feed engagement
    // caps / transcript retention and page-grounded turns. All three ride
    // `contextualChat.metadata` from the widget, and this allowlist used
    // to drop them on the floor: observed live 5 Aug 2026 on the avatar.*
    // pilot — host arrived, siteKey didn't, every turn fell to the polite
    // fallback ("companion-chat requires siteKey" in runtime logs).
    const siteKey = typeof metadata.siteKey === 'string' && metadata.siteKey.trim()
      ? (metadata.siteKey as string).trim()
      : '';
    if (siteKey) {
      payload.siteKey = siteKey;
    }
    const visitorId = typeof metadata.visitorId === 'string' && metadata.visitorId.trim()
      ? (metadata.visitorId as string).trim()
      : '';
    if (visitorId) {
      payload.visitorId = visitorId;
    }
    if (metadata.pageContext && typeof metadata.pageContext === 'object') {
      payload.pageContext = metadata.pageContext;
    }

    return payload;
  }, []);

  return {
    ensureLiveContext,
    buildContextualChatLiveRuntimeContext
  };
}

export default useLiveSessionContext;
